import { useTranslation } from "react-i18next";
import { Box, IconButton, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { AgTooltipBox, AG_TOOLTIP_HEADING_STYLE } from "@plannel/components/rechart/AgStyleTooltip";
import numberUtil from "@plannel/utils/number-util";
import { FONT_FAMILY_CSS } from "@plannel/utils/font-util";
import { UC, fmtCost } from "./llmUsageUtil";

// 생김새는 앱 공용 툴팁(AgTooltipBox)에 맡기고 MUI Tooltip 은 위치·열림만 담당한다
const POPPER_PROPS = {
  tooltip: { sx: { bgcolor: "transparent", p: 0, maxWidth: "none" } },
};

// 공용 박스의 Verdana 는 CJK 글리프가 없어 한글·일본어만 대체 폰트로 그려져 숫자와 글자 크기가 어긋난다 — 앱 언어별 폰트로 통일
const BOX_FONT = { fontFamily: FONT_FAMILY_CSS, fontVariantNumeric: "tabular-nums", lineHeight: 1.5 };

const fmtRate = (v) => numberUtil.formatNumber(v, { maximumFractionDigits: 6 });
const CELL = { textAlign: "right" };
const HEAD = { color: UC.text2 };
const RULE = { gridColumn: "1 / -1", height: 1, background: UC.line };

// 적용 환율 — 하나면 한 줄, 여럿이면 환율별 원통화·환산액 표와 합계
const RatesContent = ({ baseCurrency, rates, baseTotal }) => {
  const { t } = useTranslation();
  if (rates.length === 1) {
    const r = rates[0];
    return (
      <AgTooltipBox style={{ ...BOX_FONT, gridTemplateColumns: "auto auto" }}>
        <span>{`1 ${r.from_currency_cd} = ${fmtRate(r.exchange_rate)} ${baseCurrency}`}</span>
        <span style={HEAD}>{t("llmUsageFxAsOf", { date: r.base_date })}</span>
      </AgTooltipBox>
    );
  }
  // 원통화가 하나면 그 통화를 열 제목으로, 섞이면 통화쌍 열을 두고 금액에 통화를 붙인다
  const sources = [...new Set(rates.map((r) => r.from_currency_cd))];
  const mixed = sources.length > 1;
  const cols = mixed ? 5 : 4;
  const srcAmt = (r) => (mixed ? `${r.from_currency_cd} ` : "") + fmtCost(r.cost_amt, r.from_currency_cd);
  const srcTotal = rates.reduce((s, r) => s + Number(r.cost_amt || 0), 0);
  return (
    // 공용 박스의 최소 행 높이(1em)를 풀어 구분선 행이 1px 로 서게 한다
    <AgTooltipBox style={{ ...BOX_FONT, gridTemplateColumns: `repeat(${cols}, auto)`, gridAutoRows: "auto", columnGap: 16, rowGap: 4,
      alignItems: "baseline" }}>
      <div style={AG_TOOLTIP_HEADING_STYLE}>{t("llmUsageFxApplied")}</div>
      <span style={HEAD}>{t("baseDt")}</span>
      {mixed && <span style={HEAD} />}
      <span style={{ ...HEAD, ...CELL }}>{t("exchangeRate")}</span>
      <span style={{ ...HEAD, ...CELL }}>{mixed ? "" : sources[0]}</span>
      <span style={{ ...HEAD, ...CELL }}>{baseCurrency}</span>
      <div style={RULE} />
      {rates.map((r) => {
        const k = `${r.from_currency_cd}|${r.base_date}`;
        return [
          // 기본 통화와 같은 통화분은 환율을 쓰지 않아 기준일이 없다
          <span key={`${k}d`}>{r.base_date || "-"}</span>,
          mixed && <span key={`${k}p`} style={HEAD}>{`${r.from_currency_cd}→${baseCurrency}`}</span>,
          <span key={`${k}r`} style={CELL}>{fmtRate(r.exchange_rate)}</span>,
          <span key={`${k}a`} style={CELL}>{srcAmt(r)}</span>,
          <span key={`${k}b`} style={CELL}>{fmtCost(r.base_cost_amt, baseCurrency)}</span>,
        ];
      })}
      <div style={RULE} />
      <span style={{ gridColumn: `1 / ${mixed ? 4 : 3}`, fontWeight: 700 }}>{t("sum")}</span>
      {/* 원통화가 섞이면 원통화 합계는 더할 수 없어 비운다 */}
      <span style={{ ...CELL, fontWeight: 700 }}>{mixed ? "" : fmtCost(srcTotal, sources[0])}</span>
      <span style={{ ...CELL, fontWeight: 700, color: UC.acc }}>{fmtCost(baseTotal, baseCurrency)}</span>
    </AgTooltipBox>
  );
};

// 환산액 바로 뒤의 (i) — 호버·포커스로 적용 환율을 보여 준다
const BaseCostTooltip = ({ baseCurrency, rates, baseTotal }) => {
  const { t } = useTranslation();
  if (!(rates || []).length) return null;
  return (
    <Tooltip arrow={false} placement="top" componentsProps={POPPER_PROPS}
      title={<RatesContent baseCurrency={baseCurrency} rates={rates} baseTotal={baseTotal} />}>
      <IconButton size="small" aria-label={t("llmUsageFxApplied")}
        sx={{ p: "2px", ml: 0.25, alignSelf: "center", fontSize: "inherit", color: UC.text3,
          "&:hover, &:focus-visible": { color: UC.acc } }}>
        {/* 크기·보정 모두 글자 크기 비례(작게 12px→14px) — 줄 상자 가운데가 글자 아래 여백만큼 낮아 대문자 높이 가운데로 올린다 */}
        <Box component={InfoOutlinedIcon} sx={{ fontSize: "1.17em", transform: "translateY(-0.1em)" }} />
      </IconButton>
    </Tooltip>
  );
};

export default BaseCostTooltip;
